import React = require('react');
import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget, TabStyles, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import { useEffect, useState } from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { ResetUserMfaVerifiers } from '~src/userSettings/serverApi/requests/ResetUserMfaVerifiers';
import { IResetUserMfaVerifiersPermittedResult, ResetUserMfaVerifiersPermitted } from '~src/userSettings/serverApi/requests/ResetUserMfaVerifiersPermitted';

import { Messages } from '../../codegen/Localize';
import { IServerUserInfo } from '../../userManagement/users/models/IServerUserInfo';
import { ISiteUserInfo } from '../../userManagement/users/models/ISiteUserInfo';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';

export interface ManageMfaProps {
    serverUser: IServerUserInfo;
    changePasswordUrl: string;
    isDomainFamilyExternal: boolean;
    isDomainFamilyLocal: boolean;
    siteUser: ISiteUserInfo;
    isUserSelf: boolean;
    manageMfaUrl: string;
    isViewerAdmin: boolean;
}

export function ManageMfaUrl(manageMfaProps: ManageMfaProps): JSX.Element {
    const [isSubmitting, setSubmitting] = useState(false);
    // W-20636326: whether the current admin is permitted to reset THIS target user's MFA verifiers is
    // decided by the backend (resetMFAVerifiersPermitted). Default to permitted so the button doesn't
    // flicker enabled→disabled on load; the useEffect corrects it once the response arrives. Never
    // re-implement the backend rule on the client — just bind disabled to what the backend returns.
    const [resetPermitted, setResetPermitted] = useState(true);

    const showResetButton = manageMfaProps.isViewerAdmin && !!manageMfaProps.siteUser && manageMfaProps.siteUser.authenticationProviderId === 'TABID_WITH_MFA';
    // The Server User Account settings page (ServerUserSpace) renders this component with no siteUser,
    // so guard the id access — the dependency array below is evaluated on every render, before the
    // showResetButton early-return in the effect body runs.
    const siteUserId = manageMfaProps.siteUser && manageMfaProps.siteUser.id;

    useEffect(() => {
        if (!showResetButton) {
            return;
        }
        WebHttpClient.sendRequest(new ResetUserMfaVerifiersPermitted([siteUserId])).
            then((result: IResetUserMfaVerifiersPermittedResult) => {
                setResetPermitted(!!result && result.permitted);
            });
    }, [showResetButton, siteUserId]);

    return (
        <>
            {(!!manageMfaProps.manageMfaUrl && !!manageMfaProps.siteUser && manageMfaProps.siteUser.authenticationProviderId === 'TABID_WITH_MFA' && manageMfaProps.isUserSelf) &&
                <div className={UserSettingsStyle.accountSectionContainer}>
                    <div className={UserSettingsStyle.accountSectionLabel}></div>
                    <div className={UserSettingsStyle.accountSectionValue}>
                        <TextLinkWidget href={manageMfaProps.manageMfaUrl || '#'} kind={TextLinkKind.Standard} testId='manage-mfa-link' target='_blank' rel='noopener noreferrer'>
                            {Messages.manageMfa()}
                        </TextLinkWidget>
                    </div>
                </div>
            }
            {showResetButton &&
                <div className={UserSettingsStyle.accountSectionContainer}>
                    <div className={UserSettingsStyle.accountSectionLabel}></div>
                    <div className={UserSettingsStyle.accountSectionValue}>
                        {isSubmitting ?
                            <div style={{ marginTop: TabStyles.Spacing.SmallSpace! }}>
                                <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} testId="submitting" />
                            </div>
                            : <ButtonWidget buttonType={ButtonType.Destructive} disabled={!resetPermitted} title={!resetPermitted ? Messages.resetMfaNotPermittedDescription() : undefined} onClick={() => submit(setSubmitting, manageMfaProps)} testId='reset-mfa-button'>{Messages.resetMfa()}</ButtonWidget>
                        }
                    </div>
                </div>
            }
        </>
    );
}

const submit = (setSubmitting: (submiting: boolean) => void, props: ManageMfaProps): void => {
    const toast = ToasterService.instance;
    setSubmitting(true);
    WebHttpClient.sendRequest(new ResetUserMfaVerifiers([props.siteUser.id])).
        then((result: IResult) => {
            if (result && result.errors && result.errors.length > 0) {
                toast.pop({ type: ToastType.Error, text: Messages.resetMfaError() });
            }
            else {
                toast.pop({ type: ToastType.Info, text: Messages.resetMfaSuccess() });
            }
            setSubmitting(false);
        });
};